import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Plus, Trash2, Send, FileText, FileSignature, Receipt, Loader2,
  Link2, FileDown, Ban, ArrowRightCircle, DollarSign, CheckCircle2,
} from 'lucide-react';

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  draft: 'bg-slate-500', sent: 'bg-blue-600', viewed: 'bg-indigo-600',
  accepted: 'bg-emerald-600', signed: 'bg-emerald-600', paid: 'bg-emerald-600',
  partial: 'bg-amber-600', declined: 'bg-rose-600', voided: 'bg-rose-600',
  expired: 'bg-rose-600', converted: 'bg-teal-600', void: 'bg-rose-600',
};
const StatusBadge = ({ s }) => (
  <Badge className={`${STATUS_STYLES[s] || 'bg-slate-500'} text-white capitalize`} data-testid={`status-${s}`}>{s}</Badge>
);

const EMPTY_ITEM = { description: '', quantity: 1, unit_price: '' };

export default function AgencyTransactions() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [leads, setLeads] = useState([]);

  // Quote builder
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderKind, setBuilderKind] = useState('quote'); // quote | invoice
  const [form, setForm] = useState({ lead_id: '', title: '', items: [{ ...EMPTY_ITEM }], discount: '', tax_pct: '', notes: '', terms: '', due_date: '', valid_until: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, q, c, i, l] = await Promise.all([
        api.get('/agency/transactions/summary'),
        api.get('/agency/transactions/quotes'),
        api.get('/agency/transactions/contracts'),
        api.get('/agency/transactions/invoices'),
        api.get('/agency/crm/leads'),
      ]);
      setSummary(s.data); setQuotes(q.data || []); setContracts(c.data || []);
      setInvoices(i.data || []); setLeads((l.data || []).filter((x) => !x.is_locked));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load transactions');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const leadLabel = (lead) => [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.company_name || lead.email || 'Unnamed';
  const clientLeads = useMemo(() => leads.filter((l) => l.email), [leads]);

  const computed = useMemo(() => {
    const sub = form.items.reduce((a, it) => a + Math.round((parseFloat(it.quantity) || 0) * Math.round((parseFloat(it.unit_price) || 0) * 100)), 0);
    const disc = Math.round((parseFloat(form.discount) || 0) * 100);
    const taxable = Math.max(0, sub - disc);
    const tax = Math.round(taxable * (parseFloat(form.tax_pct) || 0) / 100);
    return { sub, disc, tax, total: taxable + tax };
  }, [form]);

  const openBuilder = (kind) => {
    setBuilderKind(kind);
    setForm({ lead_id: '', title: '', items: [{ ...EMPTY_ITEM }], discount: '', tax_pct: '', notes: '', terms: '', due_date: '', valid_until: '' });
    setBuilderOpen(true);
  };

  const setItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const saveDoc = async () => {
    if (!form.lead_id) { toast.error('Pick a CRM client'); return; }
    const items = form.items.filter((it) => it.description.trim()).map((it) => ({
      description: it.description.trim(), quantity: parseFloat(it.quantity) || 1,
      unit_price_cents: Math.round((parseFloat(it.unit_price) || 0) * 100),
    }));
    if (!items.length) { toast.error('Add at least one line item'); return; }
    const payload = {
      lead_id: form.lead_id, title: form.title || null, items,
      discount_cents: Math.round((parseFloat(form.discount) || 0) * 100),
      tax_pct: parseFloat(form.tax_pct) || 0, notes: form.notes || null,
    };
    setSaving(true);
    try {
      if (builderKind === 'quote') {
        payload.terms = form.terms || null; payload.valid_until = form.valid_until || null;
        await api.post('/agency/transactions/quotes', payload);
        toast.success('Quote created');
      } else {
        payload.due_date = form.due_date || null;
        await api.post('/agency/transactions/invoices', payload);
        toast.success('Invoice created');
      }
      setBuilderOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Action failed'); }
  };

  const copyPayLink = (inv) => {
    if (!inv.pay_url) { toast.error('Send the invoice first'); return; }
    navigator.clipboard?.writeText(inv.pay_url);
    toast.success('Payment link copied');
  };

  const downloadPdf = async (c) => {
    try {
      const res = await api.get(`/agency/transactions/contracts/${c.id}/executed.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `${c.contract_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF unavailable'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agency-transactions-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/agency/dashboard')} data-testid="tx-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Transactions</h1>
              <p className="text-sm text-slate-500">Quotes, e-signature contracts & invoices for your clients</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openBuilder('invoice')} data-testid="new-invoice-btn"><Receipt className="w-4 h-4 mr-1" /> New Invoice</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => openBuilder('quote')} data-testid="new-quote-btn"><Plus className="w-4 h-4 mr-1" /> New Quote</Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="contracts" data-testid="tab-contracts">Contracts ({contracts.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices ({invoices.length})</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card data-testid="kpi-collected"><CardContent className="p-5">
                <div className="flex items-center gap-2 text-emerald-600"><DollarSign className="w-5 h-5" /><span className="text-sm font-medium">Collected</span></div>
                <div className="text-2xl font-bold mt-2">{money(summary?.invoices?.collected_cents)}</div>
              </CardContent></Card>
              <Card data-testid="kpi-outstanding"><CardContent className="p-5">
                <div className="flex items-center gap-2 text-amber-600"><Receipt className="w-5 h-5" /><span className="text-sm font-medium">Outstanding</span></div>
                <div className="text-2xl font-bold mt-2">{money(summary?.invoices?.outstanding_cents)}</div>
                <div className="text-xs text-slate-500 mt-1">{summary?.invoices?.outstanding || 0} unpaid invoice(s)</div>
              </CardContent></Card>
              <Card data-testid="kpi-contracts"><CardContent className="p-5">
                <div className="flex items-center gap-2 text-indigo-600"><FileSignature className="w-5 h-5" /><span className="text-sm font-medium">Signed Contracts</span></div>
                <div className="text-2xl font-bold mt-2">{summary?.contracts?.signed || 0}</div>
                <div className="text-xs text-slate-500 mt-1">{summary?.contracts?.outstanding || 0} awaiting signature</div>
              </CardContent></Card>
              <Card data-testid="kpi-quotes"><CardContent className="p-5">
                <div className="flex items-center gap-2 text-blue-600"><FileText className="w-5 h-5" /><span className="text-sm font-medium">Open Quotes</span></div>
                <div className="text-2xl font-bold mt-2">{(summary?.quotes?.sent || 0) + (summary?.quotes?.accepted || 0)}</div>
                <div className="text-xs text-slate-500 mt-1">{summary?.quotes?.accepted || 0} accepted</div>
              </CardContent></Card>
            </div>
          </TabsContent>

          {/* QUOTES */}
          <TabsContent value="quotes">
            <DocTable
              rows={quotes} empty="No quotes yet — create one to send your client an itemized proposal."
              columns={['Quote', 'Client', 'Total', 'Status', '']}
              render={(q) => (
                <tr key={q.id} className="border-b hover:bg-slate-50" data-testid={`quote-row-${q.quote_number}`}>
                  <td className="py-3 px-3 font-medium">{q.quote_number}<div className="text-xs text-slate-400">{q.title}</div></td>
                  <td className="py-3 px-3">{q.client_name || q.client_email || '—'}</td>
                  <td className="py-3 px-3 font-semibold">{money(q.total_cents)}</td>
                  <td className="py-3 px-3"><StatusBadge s={q.status} /></td>
                  <td className="py-3 px-3 text-right space-x-1 whitespace-nowrap">
                    {q.status === 'draft' && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/agency/transactions/quotes/${q.id}/send`), 'Quote sent')} data-testid={`send-quote-${q.quote_number}`}><Send className="w-3.5 h-3.5 mr-1" />Send</Button>}
                    {(q.status === 'accepted' || q.status === 'sent') && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/agency/transactions/quotes/${q.id}/convert-to-contract`, {}), 'Contract created')} data-testid={`to-contract-${q.quote_number}`}><FileSignature className="w-3.5 h-3.5 mr-1" />Contract</Button>}
                    {(q.status === 'accepted' || q.status === 'sent') && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/agency/transactions/quotes/${q.id}/convert-to-invoice`, {}), 'Invoice created')} data-testid={`to-invoice-${q.quote_number}`}><ArrowRightCircle className="w-3.5 h-3.5 mr-1" />Invoice</Button>}
                    {['draft', 'declined', 'expired'].includes(q.status) && <Button size="sm" variant="ghost" onClick={() => act(() => api.delete(`/agency/transactions/quotes/${q.id}`), 'Deleted')} data-testid={`del-quote-${q.quote_number}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>}
                  </td>
                </tr>
              )}
            />
          </TabsContent>

          {/* CONTRACTS */}
          <TabsContent value="contracts">
            <DocTable
              rows={contracts} empty="No contracts yet — create one from an accepted quote to collect an e-signature."
              columns={['Contract', 'Client', 'Total', 'Status', '']}
              render={(c) => (
                <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`contract-row-${c.contract_number}`}>
                  <td className="py-3 px-3 font-medium">{c.contract_number}<div className="text-xs text-slate-400">{c.title}</div></td>
                  <td className="py-3 px-3">{c.client_name || c.client_email || '—'}</td>
                  <td className="py-3 px-3 font-semibold">{money(c.total_cents)}</td>
                  <td className="py-3 px-3"><StatusBadge s={c.status} /></td>
                  <td className="py-3 px-3 text-right space-x-1 whitespace-nowrap">
                    {['draft', 'sent', 'viewed'].includes(c.status) && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/agency/transactions/contracts/${c.id}/send`), 'Signing link sent')} data-testid={`send-contract-${c.contract_number}`}><Send className="w-3.5 h-3.5 mr-1" />{c.status === 'draft' ? 'Send' : 'Resend'}</Button>}
                    {c.status === 'signed' && <Button size="sm" variant="outline" onClick={() => downloadPdf(c)} data-testid={`pdf-contract-${c.contract_number}`}><FileDown className="w-3.5 h-3.5 mr-1" />PDF</Button>}
                    {c.status !== 'signed' && c.status !== 'voided' && <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/agency/transactions/contracts/${c.id}/void`), 'Voided')} data-testid={`void-contract-${c.contract_number}`}><Ban className="w-3.5 h-3.5 text-rose-500" /></Button>}
                  </td>
                </tr>
              )}
            />
          </TabsContent>

          {/* INVOICES */}
          <TabsContent value="invoices">
            <DocTable
              rows={invoices} empty="No invoices yet — create one or convert an accepted quote."
              columns={['Invoice', 'Client', 'Total', 'Paid', 'Status', '']}
              render={(inv) => (
                <tr key={inv.id} className="border-b hover:bg-slate-50" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <td className="py-3 px-3 font-medium">{inv.invoice_number}<div className="text-xs text-slate-400">{inv.title}</div></td>
                  <td className="py-3 px-3">{inv.client_name || inv.client_email || '—'}</td>
                  <td className="py-3 px-3 font-semibold">{money(inv.total_cents)}</td>
                  <td className="py-3 px-3">{money(inv.amount_paid_cents)}</td>
                  <td className="py-3 px-3"><StatusBadge s={inv.status} /></td>
                  <td className="py-3 px-3 text-right space-x-1 whitespace-nowrap">
                    {inv.status === 'draft' && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/agency/transactions/invoices/${inv.id}/send`), 'Invoice sent')} data-testid={`send-invoice-${inv.invoice_number}`}><Send className="w-3.5 h-3.5 mr-1" />Send</Button>}
                    {inv.public_token && inv.status !== 'paid' && <Button size="sm" variant="outline" onClick={() => copyPayLink(inv)} data-testid={`copy-link-${inv.invoice_number}`}><Link2 className="w-3.5 h-3.5 mr-1" />Pay link</Button>}
                    {inv.status === 'paid' && <span className="inline-flex items-center text-emerald-600 text-sm font-medium"><CheckCircle2 className="w-4 h-4 mr-1" />Paid</span>}
                    {inv.amount_paid_cents === 0 && inv.status !== 'paid' && <Button size="sm" variant="ghost" onClick={() => act(() => api.delete(`/agency/transactions/invoices/${inv.id}`), 'Deleted')} data-testid={`del-invoice-${inv.invoice_number}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>}
                  </td>
                </tr>
              )}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Builder dialog */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="doc-builder">
          <DialogHeader>
            <DialogTitle>New {builderKind === 'quote' ? 'Quote' : 'Invoice'}</DialogTitle>
            <DialogDescription>Build an itemized {builderKind} for one of your CRM clients.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Client</label>
              <Select value={form.lead_id} onValueChange={(v) => setForm((f) => ({ ...f, lead_id: v }))}>
                <SelectTrigger data-testid="builder-client-select"><SelectValue placeholder="Pick a CRM client (must have an email)" /></SelectTrigger>
                <SelectContent>
                  {clientLeads.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No CRM clients with an email yet</div>}
                  {clientLeads.map((l) => <SelectItem key={l.id} value={l.id}>{leadLabel(l)} · {l.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Title (e.g. Wedding Package)" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="builder-title" />

            <div className="space-y-2">
              <label className="text-sm font-medium">Line items</label>
              {form.items.map((it, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input className="flex-1" placeholder="Description" value={it.description} onChange={(e) => setItem(idx, 'description', e.target.value)} data-testid={`item-desc-${idx}`} />
                  <Input className="w-16" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, 'quantity', e.target.value)} data-testid={`item-qty-${idx}`} />
                  <Input className="w-28" type="number" placeholder="Unit $" value={it.unit_price} onChange={(e) => setItem(idx, 'unit_price', e.target.value)} data-testid={`item-price-${idx}`} />
                  <Button size="icon" variant="ghost" onClick={() => removeItem(idx)} disabled={form.items.length === 1}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Discount ($)</label><Input type="number" value={form.discount} onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))} data-testid="builder-discount" /></div>
              <div><label className="text-xs text-slate-500">Tax (%)</label><Input type="number" value={form.tax_pct} onChange={(e) => setForm((f) => ({ ...f, tax_pct: e.target.value }))} data-testid="builder-tax" /></div>
            </div>
            {builderKind === 'quote'
              ? <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-500">Valid until</label><Input type="date" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} /></div>
                  <div><label className="text-xs text-slate-500">Terms</label><Input value={form.terms} onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))} placeholder="Payment terms" /></div>
                </div>
              : <div><label className="text-xs text-slate-500">Due date</label><Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} /></div>}
            <Textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="builder-notes" />

            <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1" data-testid="builder-totals">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{money(computed.sub)}</span></div>
              {computed.disc > 0 && <div className="flex justify-between"><span className="text-slate-500">Discount</span><span>−{money(computed.disc)}</span></div>}
              {computed.tax > 0 && <div className="flex justify-between"><span className="text-slate-500">Tax</span><span>{money(computed.tax)}</span></div>}
              <div className="flex justify-between font-bold text-base pt-1 border-t"><span>Total</span><span>{money(computed.total)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuilderOpen(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={saveDoc} disabled={saving} data-testid="builder-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Create ${builderKind}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocTable({ rows, columns, render, empty }) {
  if (!rows.length) return <Card><CardContent className="p-10 text-center text-slate-400">{empty}</CardContent></Card>;
  return (
    <Card><CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-slate-50 text-left text-slate-500">
          {columns.map((c, i) => <th key={i} className="py-2.5 px-3 font-medium">{c}</th>)}
        </tr></thead>
        <tbody>{rows.map(render)}</tbody>
      </table>
    </CardContent></Card>
  );
}
